import React, { useState, useEffect, useRef } from 'react';
import { getCardStyles } from './styles';
import { calculateFontSize, handleSwipeEnd, getSwipeDirection } from './utils';
import '../CardTransition.css';

const SwipeCard = React.memo(function SwipeCard({ word, answer, incorrect, onSwipe, isVisible }) {
  const [startX, setStartX] = useState(null);
  const [offset, setOffset] = useState(0);
  const [isAnswerOnRight, setIsAnswerOnRight] = useState(Math.random() < 0.5);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const cardRef = useRef(null);
  const isActiveRef = useRef(true);

  useEffect(() => {
    setIsAnswerOnRight(Math.random() < 0.5);
    setOffset(0);
    setSwipeDirection(null);
  }, [word]);

  const handleTouchStart = (e) => {
    setStartX(e.touches[0].clientX);
  };

  const handleMouseDown = (e) => {
    setStartX(e.clientX);
  };

  const handleTouchMove = (e) => {
    if (startX === null) return;
    const currentX = e.touches[0].clientX;
    const newOffset = currentX - startX;
    setOffset(Math.min(Math.max(newOffset, -200), 200));
    setSwipeDirection(getSwipeDirection(startX, currentX));
  };

  const handleMouseMove = (e) => {
    if (startX === null) return;
    const currentX = e.clientX;
    const newOffset = currentX - startX;
    setOffset(Math.min(Math.max(newOffset, -200), 200));
    setSwipeDirection(getSwipeDirection(startX, currentX));
  };

  const handleEnd = () => {
    if (startX === null) return;
    if (!handleSwipeEnd(offset, isAnswerOnRight, onSwipe)) {
      setOffset(0);
    }
    setStartX(null);
    setSwipeDirection(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') {
      setSwipeDirection('left');
      setOffset(-200);
      onSwipe(isAnswerOnRight ? false : true);
    } else if (e.key === 'ArrowRight') {
      setSwipeDirection('right');
      setOffset(200);
      onSwipe(isAnswerOnRight ? true : false);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onSwipe, isAnswerOnRight, swipeDirection]);

  useEffect(() => {
    return () => {
      isActiveRef.current = false;
    };
  }, []);

  const styles = getCardStyles(startX, offset, isVisible);

  return (
    <div style={styles.container}>
      <div
        ref={cardRef}
        style={styles.card}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleEnd}
        onTouchCancel={handleEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
      >
        <div className={`card-content ${swipeDirection === 'left' ? 'swiping-left' : swipeDirection === 'right' ? 'swiping-right' : ''}`} style={styles.content}>
          <div style={{...styles.word, fontSize: calculateFontSize(word, true)}}>{word}</div>
          <div style={{ ...styles.split, ...styles.leftSplit }}>
            <div style={{ ...styles.meaning, fontSize: calculateFontSize(isAnswerOnRight ? incorrect : answer) }}>
              {isAnswerOnRight ? incorrect : answer}
            </div>
          </div>
          <div style={{ ...styles.split, ...styles.rightSplit }}>
            <div style={{ ...styles.meaning, fontSize: calculateFontSize(isAnswerOnRight ? answer : incorrect) }}>
              {isAnswerOnRight ? answer : incorrect}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.word === nextProps.word &&
    prevProps.answer === nextProps.answer &&
    prevProps.incorrect === nextProps.incorrect &&
    prevProps.isVisible === nextProps.isVisible
  );
});

export default SwipeCard;
